/**
 * Client-side email routing patterns.
 * Mirrors `skedyul-core` `wildcardMatch` so the feed panel can accept the same
 * values the server stores. `*.sub.example.com` is `*@*.sub.example.com`.
 */

export const EMAIL_ROUTING_PATTERN_PLACEHOLDER =
  '*@domain.com or *.sub.domain.com'

export const EMAIL_WILDCARD_FORMAT_ERROR =
  'Pattern must be an email format (e.g., *@domain.com or *.sub.domain.com)'

const SUBDOMAIN_WILDCARD_PATTERN =
  /^\*\.(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}$/i

export function isSubdomainWildcardPattern(pattern: string): boolean {
  const trimmed = pattern.trim()
  if (!trimmed || trimmed.includes('..') || /\s/.test(trimmed)) {
    return false
  }
  return SUBDOMAIN_WILDCARD_PATTERN.test(trimmed)
}

/** `*.sub.example.com` → `*@*.sub.example.com`. Other values are trimmed only. */
export function expandEmailRoutingWildcard(pattern: string): string {
  const trimmed = pattern.trim()
  if (isSubdomainWildcardPattern(trimmed)) {
    return `*@${trimmed}`
  }
  return trimmed
}

export function validateEmailWildcardPattern(pattern: string): {
  isValid: boolean
  error?: string
} {
  const trimmed = pattern.trim()
  if (!trimmed) {
    return { isValid: false, error: 'Pattern is required' }
  }

  if (!trimmed.includes('*') && !trimmed.includes('?')) {
    return { isValid: false, error: 'Pattern must contain * or ? wildcard' }
  }

  if (trimmed.includes('..')) {
    return { isValid: false, error: 'Pattern cannot contain consecutive dots' }
  }

  if (isSubdomainWildcardPattern(trimmed)) {
    return { isValid: true }
  }

  const atIndex = trimmed.indexOf('@')
  if (atIndex === -1 || atIndex === trimmed.length - 1) {
    return {
      isValid: false,
      error: EMAIL_WILDCARD_FORMAT_ERROR,
    }
  }

  const domain = trimmed.slice(atIndex + 1)
  if (!domain.includes('.')) {
    return { isValid: false, error: 'Domain must include a TLD (e.g., .com)' }
  }

  return { isValid: true }
}

export function validateFeedRoutingValue(pattern: string): {
  isValid: boolean
  error?: string
} {
  const trimmed = pattern.trim()
  if (!trimmed) {
    return { isValid: false, error: 'Routing value is required' }
  }

  if (trimmed.includes('*') || trimmed.includes('?')) {
    return validateEmailWildcardPattern(trimmed)
  }

  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
    return {
      isValid: false,
      error: 'Must be a valid email address (e.g., user@domain.com)',
    }
  }

  return { isValid: true }
}

export function getEmailRoutingMatchExamples(pattern: string): string[] {
  const expanded = expandEmailRoutingWildcard(pattern)
  if (!expanded) return []

  const dynamicSubdomain = /^\*@\*\.(.+)$/.exec(expanded)
  if (dynamicSubdomain?.[1]) {
    const suffix = dynamicSubdomain[1]
    return [`lead@gz.${suffix}`, `support@acme.${suffix}`]
  }

  if (expanded.startsWith('*@')) {
    const domain = expanded.slice(2)
    return [`john@${domain}`, `support@${domain}`, `team@${domain}`]
  }

  if (expanded.includes('*@')) {
    const [prefix, domain] = expanded.split('*@')
    return [`${prefix}123@${domain}`, `${prefix}team@${domain}`]
  }

  if (expanded.includes('*')) {
    return [expanded.replace('*', 'example')]
  }

  return []
}

/** Preview regex for the wildcard editor. Subdomain patterns are expanded first. */
export function emailRoutingRegexPreview(pattern: string): string {
  const expanded = expandEmailRoutingWildcard(pattern)
  const escaped = expanded
    .replace(/[.+^${}()|[\]\\]/g, '\\$&')
    .replace(/\*/g, '.*')
    .replace(/\?/g, '.')
  return `^${escaped}$`
}
